<script setup lang="ts">
/**
 * Thin wrapper around VeeValidate `<Form>` that:
 * - applies a sensible default vertical layout (gap-4)
 * - exposes the form context to children through scoped slot
 *
 * The actual fields are rendered with AppFormField for label/error wiring.
 */
import { Form, type GenericObject } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'
import type { ZodTypeAny } from 'zod'
import { computed, useAttrs } from 'vue'

import { cn } from '@/shared/lib/cn'

interface Props {
  schema?: ZodTypeAny
  initialValues?: GenericObject
}

const props = defineProps<Props>()

const emit = defineEmits<{
  submit: [values: GenericObject]
}>()

const attrs = useAttrs()
const typedSchema = computed(() => (props.schema ? toTypedSchema(props.schema) : undefined))
const classes = computed(() => cn('flex flex-col gap-4', (attrs.class as string) ?? ''))
</script>

<template>
  <Form
    v-slot="{ errors, isSubmitting, meta, values }"
    :validation-schema="typedSchema"
    :initial-values="initialValues"
    :class="classes"
    @submit="(v) => emit('submit', v as GenericObject)"
  >
    <!-- `values` is the live form state — exposed so a field can compute
         off another (e.g. a counterpart price). Cast as GenericObject so
         callers don't see `unknown`s in their templates. -->
    <slot
      :errors="errors"
      :is-submitting="isSubmitting"
      :meta="meta"
      :values="values as GenericObject"
    />
  </Form>
</template>
